/**
 * What a Connection is, read once.
 *
 * Two outside systems carry one: the rental system a draft is written
 * into (a **Connection**) and the calendar it appears in (a **Calendar
 * Connection**). Both come back as a name and a status, and three screens
 * had to decide from those whether anything is connected — the
 * Connections list, the Rental system page and the Calendar page — each
 * with its own test and its own copy of the names. They disagreed: the
 * server answers a rental system whose credentials are gone as its kind
 * with `disconnected`, which the list read as not connected and the
 * Rental system page drew as a connection to disconnect.
 *
 * The rule is one function now: no name, or a status of none, nothing
 * or `disconnected`, is not connected; `broken` is connected and needs
 * doing again; anything else works.
 */
import type {
  CalendarConnection,
  RentalSystem,
  RentalSystemKind,
} from "./settings-api";

export type ConnectionState = "none" | "broken" | "active";

export const RENTAL_SYSTEM_NAMES: Record<
  Exclude<RentalSystemKind, "">,
  string
> = {
  hireos: "HireOS",
  current_rms: "Current RMS",
  rentman: "Rentman",
};

export const CALENDAR_NAMES: Record<string, string> = {
  google: "Google Calendar",
  microsoft: "Outlook Calendar",
};

export interface ConnectionReading {
  state: ConnectionState;
  /** The system's name as a Company reads it, or the raw kind if unknown. */
  name: string;
}

function read(
  key: string,
  status: string,
  names: Record<string, string>,
): ConnectionReading {
  const name = names[key] ?? key;
  if (key === "" || status === "" || status === "none" || status === "disconnected") {
    return { state: "none", name };
  }
  return { state: status === "broken" ? "broken" : "active", name };
}

export const readRentalSystem = (s: RentalSystem): ConnectionReading =>
  read(s.kind, s.status, RENTAL_SYSTEM_NAMES);

export const readCalendar = (c: CalendarConnection): ConnectionReading =>
  read(c.provider, c.status, CALENDAR_NAMES);

/** One line under a Connections row: what is connected, and whether it works. */
export function connectionLine({ state, name }: ConnectionReading): string {
  if (state === "none") return "Not connected.";
  if (state === "broken") return `${name}, needs reconnecting.`;
  return `${name}, connected.`;
}
